/**
 * Phase 4 · "Add missing rows" for a continuity-mismatch block.
 * Lists the statement rows the books lack in the overlap window and, when they exactly explain the
 * balance difference, lets the user import them (plus the rest of the statement) in one click.
 * Open from anywhere with `openMissingRowsDialog(jobId, filename)`; `MissingRowsDialogHost` is mounted once.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, AlertTriangle, CheckCircle2, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/currency';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';

const API = process.env.REACT_APP_BACKEND_URL;
const OPEN_EVENT = 'akf-missing-rows-open';

export const openMissingRowsDialog = (jobId, filename) =>
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { jobId, filename } }));

const Money = ({ v, ccy }) => <span className="tabular-nums">{formatCurrency(Math.abs(Number(v || 0)), ccy || 'NGN')}</span>;

const RowList = ({ rows, ccy, testId }) => (
  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-56 overflow-y-auto" data-testid={testId}>
    {rows.map((r, i) => (
      <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
        <span className="text-xs text-slate-500 w-[5.5rem] shrink-0 tabular-nums">{r.date}</span>
        <span className="flex-1 min-w-0 truncate text-slate-700" title={r.description}>{r.description || '—'}</span>
        <span className={`shrink-0 font-medium ${r.direction === 'inflow' ? 'text-emerald-700' : 'text-red-700'}`}>
          {r.direction === 'inflow' ? '+' : '−'}<Money v={r.amount} ccy={ccy} />
        </span>
      </li>
    ))}
  </ul>
);

export const MissingRowsDialog = ({ open, onOpenChange, jobId, filename }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const { addJob } = useBackgroundJobs();

  useEffect(() => {
    if (!open || !jobId) return;
    setData(null); setLoading(true);
    axios.get(`${API}/api/bank-feeds/upload-jobs/${jobId}/missing-rows`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load the missing-row analysis'))
      .finally(() => setLoading(false));
  }, [open, jobId]);

  const a = data?.analysis;
  const ccy = data?.currency;

  const addRows = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/api/bank-feeds/upload-jobs/${jobId}/add-missing-rows`);
      toast.success(res.message || 'Missing rows are being added.');
      addJob({ jobId, filename: filename || data?.filename, status: 'resuming', progress: 20 });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add the missing rows');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl w-[94vw]" data-testid="missing-rows-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListPlus className="w-5 h-5 text-[#283F8B]" /> Continuity mismatch — missing rows</DialogTitle>
          <DialogDescription className="truncate">{filename || data?.filename}</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}

        {!loading && data && !a && (
          <p className="text-sm text-slate-600" data-testid="missing-rows-none">
            {data.added ? `Already resolved — ${data.added.missing_count} missing row(s) were added to this upload.`
              : 'No missing-row analysis is available for this upload. Re-upload the statement to run the check again.'}
          </p>
        )}

        {!loading && a && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-xs">
              {[['Books on ' + (data.last_close_date || '—'), data.books_balance], ['Statement', data.statement_balance], ['Difference', data.delta]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 border border-slate-200 p-2">
                  <div className="text-slate-500 truncate">{l}</div>
                  <div className="font-semibold text-slate-800 tabular-nums">{v == null ? '—' : (Number(v) < 0 ? '−' : '') }{v == null ? '' : <Money v={v} ccy={ccy} />}</div>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-sm font-medium text-slate-800">On the statement, not in your books <span className="text-slate-400">({a.missing_count})</span></h4>
                <span className="text-xs text-slate-500">Net {a.missing_sum < 0 ? '−' : '+'}<Money v={a.missing_sum} ccy={ccy} /></span>
              </div>
              {a.missing_count ? <RowList rows={a.missing_rows} ccy={ccy} testId="missing-rows-list" /> : <p className="text-xs text-slate-500">None.</p>}
              {a.missing_count > a.missing_rows.length && <p className="text-xs text-slate-500 mt-1">+{a.missing_count - a.missing_rows.length} more</p>}
            </div>

            {a.extra_count > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-800 mb-1.5">In your books, not on the statement <span className="text-slate-400">({a.extra_count})</span></h4>
                <RowList rows={a.extra_books_rows} ccy={ccy} testId="extra-books-rows-list" />
              </div>
            )}

            {a.can_add ? (
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="missing-rows-can-add">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>These {a.missing_count} row(s) explain the difference exactly. Adding them will reconcile the balance and import the rest of the statement.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="missing-rows-cannot-add">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{a.cannot_add_reason}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="missing-rows-close-btn">Close</Button>
          {a?.can_add && data?.status === 'blocked_continuity' && (
            <Button onClick={addRows} disabled={busy} className="bg-[#283F8B] hover:bg-[#1f326f]" data-testid="missing-rows-add-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ListPlus className="w-4 h-4 mr-2" />}
              Add {a.missing_count} missing row{a.missing_count === 1 ? '' : 's'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Mount once (DashboardLayout). */
export default function MissingRowsDialogHost() {
  const [state, setState] = useState({ open: false, jobId: null, filename: null });
  useEffect(() => {
    const h = (e) => setState({ open: true, jobId: e.detail?.jobId, filename: e.detail?.filename });
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);
  return <MissingRowsDialog open={state.open} onOpenChange={(o) => setState((s) => ({ ...s, open: o }))} jobId={state.jobId} filename={state.filename} />;
}
